import { fetchApi, type GenericRes } from '@hunk/shared/api/fetchApi';
import type { SitePageLink } from '@hunk/shared/api/pages';
import type { DeliveryMethodType } from '@hunk/shared/common';

/** The public shop: categories, products, delivery options. Under /api/shop, no login. Money in agorot. */

export interface ShopCategory {
  id: number;
  name: string;
  slug: string;
}

export interface ShopProduct {
  id: number;
  name: string;
  description: string;
  sku: string | null;
  /** regular price */
  price: number;
  /** sale price (lower than price) or null */
  salePrice: number | null;
  inStock: boolean;
  /** units left when tracked and low (<= 5), else null */
  lowStock: number | null;
  /** max units one order can take (stock when tracked) */
  maxQty: number;
  categoryIds: number[];
  /** image URLs (/uploads/...), main first */
  images: string[];
  createdOn: string;
}

/** the price a customer pays now */
export const effectivePrice = (p: Pick<ShopProduct, 'price' | 'salePrice'>) => (p.salePrice != null && p.salePrice < p.price ? p.salePrice : p.price);

/**
 * One way to get the order (admin: משלוחים והגדרות - the admin adds as many as needed, the first is preselected at checkout).
 * courier = to the door, post = to the post office near the customer's address (both ask for the address), pickup = the customer collects it.
 */
export interface DeliveryOption {
  id: string;
  kind: DeliveryMethodType;
  /** "שליח עד הבית", "דואר רשום", "איסוף מהחנות בתל אביב" */
  name: string;
  /** pickup: address + hours; courier: e.g. delivery time */
  description: string;
  /** agorot, 0 = free */
  fee: number;
  /** items total (agorot) from which this option is free; null = never */
  freeFrom: number | null;
  /** off = kept in the settings but not offered */
  enabled: boolean;
}

/** delivery settings the checkout needs (admin: הגדרות חנות); the public catalog gets only the enabled options */
export interface ShopDeliverySettings {
  options: DeliveryOption[];
  /** extra charge for discreet packaging (agorot, 0 = free) */
  discreetFee: number;
  /** minimum order (agorot); 0 = none */
  minOrder: number;
}

export interface CatalogRes extends GenericRes {
  categories?: ShopCategory[];
  products?: ShopProduct[];
  delivery?: ShopDeliverySettings;
  /** content pages for the top line + footer */
  pages?: SitePageLink[];
}

export const getCatalog = () => fetchApi<{}, CatalogRes>({ apiPath: '/api/shop/catalog' });

/** the option's fee for this cart: free from its freeFrom */
export const deliveryFee = (o: Pick<DeliveryOption, 'fee' | 'freeFrom'>, itemsTotal: number): number => (o.freeFrom != null && itemsTotal >= o.freeFrom ? 0 : o.fee);

/** shipping for a cart: the option's fee (unless free shipping applies) + discreet fee */
export const shippingFor = (d: ShopDeliverySettings, o: DeliveryOption, itemsTotal: number, discreet: boolean): number =>
  deliveryFee(o, itemsTotal) + (discreet ? d.discreetFee : 0);
